import { useState } from 'react';
import type { FormEvent } from 'react';
import { ChevronLeft, Smartphone } from 'lucide-react';
import { formatPhone, isValidMobile, normalizePhone } from '../../core/phone';
import { GROUP_PASSWORD_LENGTH, isValidGroupPassword } from '../../core/cloud-auth';
import { MOCK_DEFAULT_PINS, MOCK_USERS } from '../../core/mock-users';
import { ROLE_LABELS } from '../../core/permissions';
import { AppSignature, AuthShell } from './AuthShell';

interface PhoneEntryStepProps {
  onSubmit: (phone: string, password: string) => void;
  busy?: boolean;
  error?: string | null;
}

/**
 * Step 1 — the phone number and the group's shared password.
 *
 * The pair is what `/api/login` checks against the central directory: the
 * password says "one of us", the number says who. A device that cannot reach
 * the directory falls back to the local flow behind this screen (see
 * `AuthFlow.tsx`), which is what keeps a phone with no signal working.
 */
export function PhoneEntryStep({ onSubmit, busy = false, error = null }: PhoneEntryStepProps) {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);

  const normalized = normalizePhone(phone);
  const valid = isValidMobile(normalized);
  const passwordValid = isValidGroupPassword(password);
  const showError = touched && normalized.length > 0 && !valid;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!valid || !passwordValid || busy) return;
    onSubmit(normalized, password);
  };

  return (
    <AuthShell
      icon={<Smartphone size={22} aria-hidden />}
      title="כניסה למערכת"
      subtitle="הזינו מספר טלפון נייד וסיסמת הקבוצה"
      footer={<AppSignature />}
    >
      <form onSubmit={handleSubmit} noValidate>
        <label htmlFor="phone" className="block text-sm font-medium mb-2">
          מספר טלפון
        </label>
        <input
          id="phone"
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          placeholder="050-000-0000"
          maxLength={13}
          value={formatPhone(normalized)}
          onChange={(event) => setPhone(normalizePhone(event.target.value).slice(0, 10))}
          onBlur={() => setTouched(true)}
          disabled={busy}
          aria-invalid={showError || Boolean(error)}
          data-dir="ltr"
          className={[
            'w-full h-12 px-4 rounded-xl border bg-white text-lg tracking-wide',
            'outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-100',
            showError || error ? 'border-red-400' : 'border-line',
          ].join(' ')}
        />

        {showError ? (
          <p className="mt-2 text-sm text-red-600">מספר לא תקין — נדרש מספר נייד ישראלי (05XXXXXXXX)</p>
        ) : null}

        <label htmlFor="group-password" className="block text-sm font-medium mt-4 mb-2">
          סיסמת הקבוצה
        </label>
        <input
          id="group-password"
          type="password"
          inputMode="numeric"
          autoComplete="current-password"
          placeholder={'•'.repeat(GROUP_PASSWORD_LENGTH)}
          maxLength={GROUP_PASSWORD_LENGTH}
          value={password}
          onChange={(event) =>
            setPassword(event.target.value.replace(/\D+/g, '').slice(0, GROUP_PASSWORD_LENGTH))
          }
          disabled={busy}
          aria-invalid={Boolean(error)}
          data-dir="ltr"
          className={[
            'w-full h-12 px-4 rounded-xl border bg-white text-lg tracking-[0.4em] text-center',
            'outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-100',
            error ? 'border-red-400' : 'border-line',
          ].join(' ')}
        />
        <p className="mt-2 text-xs text-ink/50">
          סיסמה משותפת לכל היחידה, בת {GROUP_PASSWORD_LENGTH} ספרות. הזהות עצמה נקבעת לפי מספר
          הטלפון הרשום בספריית המשתמשים.
        </p>

        {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}

        <button
          type="submit"
          disabled={!valid || !passwordValid || busy}
          className="mt-5 w-full h-12 rounded-xl bg-brand-500 text-white font-semibold transition-colors hover:bg-brand-700 disabled:opacity-40 disabled:hover:bg-brand-500 flex items-center justify-center gap-2"
        >
          {busy ? 'רגע…' : 'כניסה'}
          {busy ? null : <ChevronLeft size={18} aria-hidden />}
        </button>
      </form>

      <details className="mt-5 text-sm">
        <summary className="cursor-pointer text-ink/60 select-none">משתמשי בדיקה</summary>
        <ul className="mt-2 space-y-1">
          {MOCK_USERS.map((user) => (
            <li key={user.id} className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setPhone(user.phone)}
                className="text-brand-700 hover:underline"
                data-dir="ltr"
              >
                {formatPhone(user.phone)}
              </button>
              <span className="text-ink/60">
                {user.fullName} · {ROLE_LABELS[user.role]}
                {MOCK_DEFAULT_PINS[user.id] ? ` · קוד ${MOCK_DEFAULT_PINS[user.id]}` : null}
              </span>
            </li>
          ))}
        </ul>
      </details>
    </AuthShell>
  );
}
